import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import PageTitle from '../components/PageTitle';
import {
  BookOpen,
  Calendar,
  Clock,
  Tag,
  ArrowRight,
  Search,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import api from '../services/api';
import { toast } from 'react-toastify';

const HomePage = () => {
  const [posts, setPosts] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState(null);

  useEffect(() => {
    fetchPosts();
    fetchTags();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await api.get('/posts?status=published&limit=1000');
      const posts = response.data.data?.posts || [];
      setPosts(posts);
    } catch (error) {
      console.error('Erro ao buscar posts:', error);
      toast.error('Erro ao carregar os estudos');
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await api.get('/tags');
      setTags(response.data.data?.tags || []);
    } catch (error) {
      console.error('Erro ao buscar tags:', error);
    }
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt?.toLowerCase().includes(searchTerm.toLowerCase());

    // Convertendo para string para garantir comparação correta
    const matchesTag = !selectedTag || (post.tags && post.tags.length > 0 && post.tags.some(tag => String(tag.id) === String(selectedTag)));

    return matchesSearch && matchesTag;
  });

  const formatDate = (date) => {
    try {
      return format(new Date(date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
    } catch {
      return '';
    }
  };

  return (
    <>
      <PageTitle
        title="Estudos Bíblicos"
        description="Crescimento espiritual através do estudo da Palavra de Deus"
      />

      <div className="min-h-screen bg-black text-white">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-church-green/20 to-black z-0" />
          <div className="container relative z-10 mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center space-y-6">
              <h1 className="text-5xl md:text-6xl font-heading font-bold text-white tracking-tight animate-fade-in">
                Estudos Bíblicos
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 font-light font-sans max-w-2xl mx-auto leading-relaxed animate-fade-in animation-delay-100">
                Descubra, aprenda e seja transformado pela <span className="text-church-green font-medium">Palavra de Deus</span>
              </p>

              {/* Barra de Busca */}
              <div className="relative max-w-xl mx-auto pt-8 animate-fade-in animation-delay-200">
                <div className="relative">
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 z-10 pointer-events-none">
                    <Search className="h-5 w-5" />
                  </div>
                  <Input
                    type="text"
                    placeholder="Buscar estudos..."
                    className="pl-12 pr-4 py-6 w-full text-white bg-white/5 border-white/10 rounded-full focus:ring-2 focus:ring-church-green focus:border-transparent placeholder:text-gray-500 transition-all"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Tags/Categorias */}
        {tags.length > 0 && (
          <section className="py-8 border-b border-white/5">
            <div className="container mx-auto px-4">
              <div className="flex flex-wrap gap-3 justify-center items-center">
                <span className="text-sm font-medium text-gray-400 mr-2 uppercase tracking-widest">Categorias:</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedTag(null)}
                  className={`rounded-full border ${!selectedTag ? 'bg-white text-black border-white' : 'bg-transparent text-gray-300 border-white/20 hover:border-white hover:text-white'}`}
                >
                  Todas
                </Button>
                {tags.map(tag => (
                  <Button
                    key={tag.id}
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedTag(tag.id)}
                    className="rounded-full transition-all duration-300"
                    style={{
                      backgroundColor: selectedTag === tag.id ? tag.color : 'transparent',
                      borderColor: selectedTag === tag.id ? tag.color : 'rgba(255,255,255,0.2)',
                      borderWidth: '1px',
                      color: selectedTag === tag.id ? '#fff' : tag.color
                    }}
                  >
                    <Tag className="h-3 w-3 mr-1" />
                    {tag.name}
                  </Button>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Lista de Posts */}
        <section className="py-16 bg-black/50">
          <div className="container mx-auto px-4">
            {loading ? (
              <div className="text-center py-20">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-church-green"></div>
                <p className="mt-4 text-gray-500 font-light">Carregando estudos...</p>
              </div>
            ) : filteredPosts.length === 0 ? (
              <div className="text-center py-20 border border-dashed border-white/10 rounded-xl bg-white/5">
                <BookOpen className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-heading text-white mb-2">
                  {searchTerm ? 'Nenhum estudo encontrado' : 'Nenhum estudo publicado ainda'}
                </h3>
                <p className="text-gray-400">
                  {searchTerm ? 'Tente buscar por outros termos' : 'Volte em breve para novos conteúdos'}
                </p>
              </div>
            ) : (
              <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 max-w-7xl mx-auto">
                {filteredPosts.map(post => (
                  <Card key={post.id} className="group bg-[#0a0a0a] border-white/10 overflow-hidden hover:border-church-green/50 transition-all duration-500 hover:shadow-2xl hover:shadow-church-green/10 flex flex-col h-full">
                    {post.featuredImage && (
                      <div className="aspect-video w-full overflow-hidden relative">
                        <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-all duration-500 z-10" />
                        <img
                          src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001'}/${post.featuredImage}`}
                          alt={post.title}
                          className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 filter grayscale-[20%] group-hover:grayscale-0"
                        />
                      </div>
                    )}
                    <CardHeader className="space-y-3 pb-2 flex-grow-0">
                      <div className="flex items-center justify-between text-xs text-church-gray">
                        <span className="flex items-center gap-1.5 bg-white/5 px-2 py-1 rounded">
                          <Calendar className="h-3 w-3" />
                          {formatDate(post.publishedAt)}
                        </span>
                        {post.readTime && (
                          <span className="flex items-center gap-1.5">
                            <Clock className="h-3 w-3" />
                            {post.readTime} min
                          </span>
                        )}
                      </div>
                      <CardTitle className="text-2xl font-heading leading-tight pt-1">
                        <Link
                          to={`/posts/${post.slug}`}
                          className="text-white group-hover:text-church-green transition-colors duration-300 block"
                        >
                          {post.title}
                        </Link>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 flex-grow flex flex-col justify-between">
                      <CardDescription className="text-gray-400 line-clamp-3 font-sans text-sm leading-relaxed">
                        {post.excerpt || 'Clique para ler o estudo completo...'}
                      </CardDescription>

                      <div className="pt-4 mt-auto space-y-4">
                        {post.tags && post.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {post.tags.slice(0, 3).map(tag => (
                              <Link
                                key={tag.id}
                                to={`/tag/${tag.slug}`}
                                className="inline-flex items-center px-2 py-1 rounded text-[10px] font-medium tracking-wider uppercase transition-colors hover:bg-white/10"
                                style={{
                                  color: tag.color,
                                  border: `1px solid ${tag.color}40`
                                }}
                              >
                                {tag.name}
                              </Link>
                            ))}
                          </div>
                        )}

                        <Link to={`/posts/${post.slug}`} className="block">
                          <Button variant="ghost" className="w-full justify-between text-white hover:text-church-green hover:bg-white/5 group-hover:pl-6 transition-all duration-300 border border-white/10">
                            Ler estudo
                            <ArrowRight className="h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Call to Action Simples */}
        <section className="py-20 bg-gradient-to-t from-[#050505] to-black border-t border-white/5">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-heading text-white mb-4">
              Acompanhe nossos estudos
            </h2>
            <p className="text-lg text-gray-400 mb-8 max-w-2xl mx-auto font-light">
              Novos conteúdos são publicados regularmente para ajudar em sua jornada espiritual
            </p>
            <div className="flex gap-4 justify-center">
              <Link to="/sobre">
                <Button variant="outline" size="lg" className="border-white/20 text-white hover:bg-white hover:text-black transition-colors">
                  Sobre o projeto
                </Button>
              </Link>
              <Link to="/contato">
                <Button size="lg" className="bg-church-green text-white hover:bg-church-green/90 border-none">
                  Entre em contato
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default HomePage;